import TextInput from "Components/molecule/inputs/text-input"
import SearchSelect from "Components/molecule/inputs/search-select"
import { SelectOption } from "Components/molecule/inputs/select-input"
import PrimaryButton from "Components/atoms/buttons/primary-button"
import SecondaryButton from "Components/atoms/buttons/secondary-button"
import FormContainer from "Components/atoms/containers/form-container"
import FormProps from "data/forms"
import DepartmentKPIFields from "./types"

interface DepartmentKPIFormProps extends FormProps<DepartmentKPIFields> {
    fiscalYears: SelectOption[]
    branches: SelectOption[]
}

const COL = "col-12 col-md-6 col-lg-4"

const BRANCH_KEY = "branchId";
const FISCAL_YEAR_KEY = "fiscalYear";
const BUDGET_KEY = "budget";
const ANNUAL_REVENUE_KEY = "annualRevenue";
const GROSS_MARGIN_KEY = "grossMargin";
const WORK_IN_HAND_KEY = "workInHand";
const BLUE_ZONE_KEY = "blueZone";
const YELLOW_ZONE_KEY = "yellowZone";
const OPPORTUNITY_WIN_TOTAL_KEY = "opportunityWinTotal";
const PROJECT_PROFITABILITY_KEY = "projectProfitability";
const PERFORMANCE_MANAGEMENT_KEY = "performanceManagement";

const DepartmentKPIForm = ({
    values,
    handleChange,
    handleBlur,
    handleSubmit,
    handleCancel,
    errors,
    isValid,
    touched,
    isSubmitting,
    fiscalYears,
    branches,
    edit
}: DepartmentKPIFormProps) => {

    return <FormContainer
        buttonsClassName="d-flex flex-column flex-md-row gap-2"
        inputs={<>
            {
                // Branch is part of the primary key, so it cannot move once the row exists.
                edit
                    ? <TextInput
                        className={COL}
                        name={BRANCH_KEY}
                        key={BRANCH_KEY}
                        label={"Branch"}
                        value={branches.find(b => b.value === values.branchId)?.label ?? values.branchId}
                        readOnly
                        disabled
                    />
                    : <SearchSelect
                        className={COL}
                        name={BRANCH_KEY}
                        key={BRANCH_KEY}
                        label={"Branch*"}
                        placeholder={"Select a branch"}
                        value={values.branchId || ''}
                        error={!!touched.branchId && !!errors.branchId}
                        message={touched.branchId ? errors.branchId : undefined}
                        options={branches}
                        onChange={(option: SelectOption | null) => handleChange(BRANCH_KEY)(option ? option.value : '')}
                        onBlur={handleBlur(BRANCH_KEY)}
                    />
            }

            {
                // Fiscal year is half the primary key, so it cannot move once the row exists.
                edit
                    ? <TextInput
                        className={COL}
                        name={FISCAL_YEAR_KEY}
                        key={FISCAL_YEAR_KEY}
                        label={"Fiscal Year"}
                        value={values.fiscalYear}
                        readOnly
                        disabled
                    />
                    : <SearchSelect
                        className={COL}
                        name={FISCAL_YEAR_KEY}
                        key={FISCAL_YEAR_KEY}
                        label={"Fiscal Year*"}
                        placeholder={"Select a fiscal year"}
                        value={values.fiscalYear === '' ? '' : String(values.fiscalYear)}
                        error={!!touched.fiscalYear && !!errors.fiscalYear}
                        message={touched.fiscalYear ? errors.fiscalYear : undefined}
                        options={fiscalYears}
                        onChange={(option: SelectOption | null) => handleChange(FISCAL_YEAR_KEY)(option ? option.value : '')}
                        onBlur={handleBlur(FISCAL_YEAR_KEY)}
                    />
            }

            <TextInput
                className={COL}
                touched={!!touched.budget}
                name={BUDGET_KEY}
                key={BUDGET_KEY}
                type="number"
                label={"Budget*"}
                placeholder={"e.g. 1500000"}
                value={values.budget}
                onBlur={handleBlur}
                onChange={handleChange}
                error={!!touched.budget && !!errors.budget}
                message={touched.budget ? errors.budget : undefined}
            />

            <TextInput
                className={COL}
                touched={!!touched.annualRevenue}
                name={ANNUAL_REVENUE_KEY}
                key={ANNUAL_REVENUE_KEY}
                type="number"
                label={"Annual Revenue*"}
                placeholder={"e.g. 2000000"}
                value={values.annualRevenue}
                onBlur={handleBlur}
                onChange={handleChange}
                error={!!touched.annualRevenue && !!errors.annualRevenue}
                message={touched.annualRevenue ? errors.annualRevenue : undefined}
            />

            <TextInput
                className={COL}
                touched={!!touched.grossMargin}
                name={GROSS_MARGIN_KEY}
                key={GROSS_MARGIN_KEY}
                type="number"
                label={"Gross Margin*"}
                placeholder={"e.g. 350000"}
                value={values.grossMargin}
                onBlur={handleBlur}
                onChange={handleChange}
                error={!!touched.grossMargin && !!errors.grossMargin}
                message={touched.grossMargin ? errors.grossMargin : undefined}
            />

            <TextInput
                className={COL}
                touched={!!touched.workInHand}
                name={WORK_IN_HAND_KEY}
                key={WORK_IN_HAND_KEY}
                type="number"
                label={"Work In Hand*"}
                placeholder={"e.g. 750000"}
                value={values.workInHand}
                onBlur={handleBlur}
                onChange={handleChange}
                error={!!touched.workInHand && !!errors.workInHand}
                message={touched.workInHand ? errors.workInHand : undefined}
            />

            <TextInput
                className={COL}
                touched={!!touched.blueZone}
                name={BLUE_ZONE_KEY}
                key={BLUE_ZONE_KEY}
                type="number"
                label={"Blue Zone*"}
                placeholder={"e.g. 850000"}
                value={values.blueZone}
                onBlur={handleBlur}
                onChange={handleChange}
                error={!!touched.blueZone && !!errors.blueZone}
                message={touched.blueZone ? errors.blueZone : undefined}
            />

            <TextInput
                className={COL}
                touched={!!touched.yellowZone}
                name={YELLOW_ZONE_KEY}
                key={YELLOW_ZONE_KEY}
                type="number"
                label={"Yellow Zone*"}
                placeholder={"e.g. 700000"}
                value={values.yellowZone}
                onBlur={handleBlur}
                onChange={handleChange}
                error={!!touched.yellowZone && !!errors.yellowZone}
                message={touched.yellowZone ? errors.yellowZone : undefined}
            />

            <TextInput
                className={COL}
                touched={!!touched.opportunityWinTotal}
                name={OPPORTUNITY_WIN_TOTAL_KEY}
                key={OPPORTUNITY_WIN_TOTAL_KEY}
                type="number"
                label={"Opportunity Win Total*"}
                placeholder={"e.g. 500000"}
                value={values.opportunityWinTotal}
                onBlur={handleBlur}
                onChange={handleChange}
                error={!!touched.opportunityWinTotal && !!errors.opportunityWinTotal}
                message={touched.opportunityWinTotal ? errors.opportunityWinTotal : undefined}
            />

            <TextInput
                className={COL}
                touched={!!touched.projectProfitability}
                name={PROJECT_PROFITABILITY_KEY}
                key={PROJECT_PROFITABILITY_KEY}
                type="number"
                label={"Project Profitability*"}
                placeholder={"e.g. 200000"}
                value={values.projectProfitability}
                onBlur={handleBlur}
                onChange={handleChange}
                error={!!touched.projectProfitability && !!errors.projectProfitability}
                message={touched.projectProfitability ? errors.projectProfitability : undefined}
            />

            <TextInput
                className={COL}
                touched={!!touched.performanceManagement}
                name={PERFORMANCE_MANAGEMENT_KEY}
                key={PERFORMANCE_MANAGEMENT_KEY}
                label={"Performance Management*"}
                placeholder={"e.g. Quarterly reviews complete"}
                value={values.performanceManagement}
                onBlur={handleBlur}
                onChange={handleChange}
                error={!!touched.performanceManagement && !!errors.performanceManagement}
                message={touched.performanceManagement ? errors.performanceManagement : undefined}
            />

            <div className="col-12 mt-2">
                <h6 className="mb-0">Company targets (optional)</h6>
                <small className="text-muted">
                    Used by Finance &gt; Financial KPI Metrics and read only from the company (top level) department. Leave blank on other departments.
                </small>
            </div>

            <TextInput
                className={COL}
                touched={!!touched.arOverdueTarget}
                name="arOverdueTarget"
                key="arOverdueTarget"
                type="number"
                label={"AR Overdue > 120 Days ($)"}
                placeholder={"e.g. 1000000"}
                value={values.arOverdueTarget}
                onBlur={handleBlur}
                onChange={handleChange}
                error={!!touched.arOverdueTarget && !!errors.arOverdueTarget}
                message={touched.arOverdueTarget ? errors.arOverdueTarget : undefined}
            />

            <TextInput
                className={COL}
                touched={!!touched.turnoverTarget}
                name="turnoverTarget"
                key="turnoverTarget"
                type="number"
                label={"Turnover Max (%)"}
                placeholder={"e.g. 12"}
                value={values.turnoverTarget}
                onBlur={handleBlur}
                onChange={handleChange}
                error={!!touched.turnoverTarget && !!errors.turnoverTarget}
                message={touched.turnoverTarget ? errors.turnoverTarget : undefined}
            />

            <TextInput
                className={COL}
                touched={!!touched.winRateTarget}
                name="winRateTarget"
                key="winRateTarget"
                type="number"
                label={"Proposal Win Rate (%)"}
                placeholder={"e.g. 40"}
                value={values.winRateTarget}
                onBlur={handleBlur}
                onChange={handleChange}
                error={!!touched.winRateTarget && !!errors.winRateTarget}
                message={touched.winRateTarget ? errors.winRateTarget : undefined}
            />
        </>}
        buttons={<>
            <PrimaryButton
                className="col-12 col-md-6 col-lg-2"
                type='submit'
                onClick={() => handleSubmit()}
                disabled={isSubmitting || !isValid}
            >
                {isSubmitting ? "Saving...." : edit ? "Update" : "Save"}
            </PrimaryButton>
            {
                handleCancel &&
                <SecondaryButton
                    className="col-12 col-md-6 col-lg-2"
                    disabled={isSubmitting}
                    onClick={() => handleCancel()}
                >
                    Back
                </SecondaryButton>
            }
        </>}
    />
}

export default DepartmentKPIForm
